<template>
    <div class="tab-panel">
        <div class="row g-3 mb-3">
            <div class="col-12 col-sm-6 col-xl-3">
                <StatCard label="Missions" :value="stats.currentPeriod.missionsCount" :subtitle="periodSubtitle"
                    :trend="missionsTrend" />
            </div>

            <div class="col-12 col-sm-6 col-xl-3">
                <StatCard label="Alarmes" :value="stats.currentPeriod.alarmsCount" :subtitle="periodSubtitle"
                    :trend="alarmsTrend" />
            </div>

            <div class="col-12 col-sm-6 col-xl-3">
                <StatCard label="Moy. alarmes / mission" :value="formattedAverageAlarmsPerMission"
                    :subtitle="averageAlarmsPerMissionSubtitle" :trend="averageAlarmsPerMissionTrend" />
            </div>

            <div class="col-12 col-sm-6 col-xl-3">
                <StatCard label="Devices surveillés" :value="stats.currentPeriod.balisesCount"
                    subtitle="Sur l’ensemble du parc" :trend="balisesTrend" />
            </div>
        </div>

        <div class="row g-3">
            <div class="col-12 col-xl-8">
                <div class="card shadow-sm panel">
                    <div class="card-body">
                        <div class="panel-header mb-3">
                            <div>
                                <div class="panel-title">Évolution des anomalies</div>
                                <div class="panel-subtitle text-muted small">
                                    Alarmes par jour sur les {{ days * 2 }} derniers jours
                                </div>
                            </div>
                        </div>

                        <div class="chart-placeholder chart-placeholder-lg">
                            <VChart v-if="stats.currentPeriod.criticalAlarmsTimeline.length" class="main-chart"
                                :option="criticalAlarmsTimelineChartOptions" autoresize />
                            <div v-else class="text-muted small">
                                Aucune donnée disponible sur la période
                            </div>
                        </div>
                    </div>
                </div>

                <div class="card shadow-sm panel mt-3">
                    <div class="card-body">
                        <div class="panel-header mb-3">
                            <div>
                                <div class="panel-title">Activité missions</div>
                                <div class="panel-subtitle text-muted small">
                                    Données calculées sur les {{ days }} derniers jours
                                </div>
                            </div>
                        </div>

                        <div class="mission-analytics">
                            <div class="mission-analytics-column">
                                <div class="mission-analytics-title">Durées</div>

                                <div class="mission-analytics-block">
                                    <div class="mission-analytics-value-row">
                                        <div class="mission-analytics-value">{{ formattedTotalDuration }}</div>

                                        <div v-if="totalDurationTrend && totalDurationTrend.label"
                                            class="mission-analytics-trend"
                                            :class="getTrendToneClass(totalDurationTrend.tone)">
                                            <font-awesome-icon v-if="totalDurationTrend.direction === 'up'"
                                                :icon="['fas', 'arrow-trend-up']"
                                                class="mission-analytics-trend-icon" />
                                            <font-awesome-icon v-else-if="totalDurationTrend.direction === 'down'"
                                                :icon="['fas', 'arrow-trend-down']"
                                                class="mission-analytics-trend-icon" />
                                            <font-awesome-icon v-else :icon="['fas', 'minus']"
                                                class="mission-analytics-trend-icon" />
                                            <span>{{ totalDurationTrend.label }}</span>
                                        </div>
                                    </div>

                                    <div class="mission-analytics-label">Durée totale</div>
                                    <div class="mission-analytics-subtitle">Temps cumulé en mission</div>
                                </div>

                                <div class="mission-analytics-block">
                                    <div class="mission-analytics-value-row">
                                        <div class="mission-analytics-value">{{ formattedAverageDuration }}</div>

                                        <div v-if="averageDurationTrend && averageDurationTrend.label"
                                            class="mission-analytics-trend"
                                            :class="getTrendToneClass(averageDurationTrend.tone)">
                                            <font-awesome-icon v-if="averageDurationTrend.direction === 'up'"
                                                :icon="['fas', 'arrow-trend-up']"
                                                class="mission-analytics-trend-icon" />
                                            <font-awesome-icon v-else-if="averageDurationTrend.direction === 'down'"
                                                :icon="['fas', 'arrow-trend-down']"
                                                class="mission-analytics-trend-icon" />
                                            <font-awesome-icon v-else :icon="['fas', 'minus']"
                                                class="mission-analytics-trend-icon" />
                                            <span>{{ averageDurationTrend.label }}</span>
                                        </div>
                                    </div>

                                    <div class="mission-analytics-label">Durée moyenne</div>
                                    <div class="mission-analytics-subtitle">Par mission terminée</div>
                                </div>
                            </div>

                            <div class="mission-analytics-separator"></div>

                            <div class="mission-analytics-column">
                                <div class="mission-analytics-title">Distances</div>

                                <div class="mission-analytics-block">
                                    <div class="mission-analytics-value-row">
                                        <div class="mission-analytics-value">{{ formattedTotalDistance }}</div>

                                        <div v-if="totalDistanceTrend && totalDistanceTrend.label"
                                            class="mission-analytics-trend"
                                            :class="getTrendToneClass(totalDistanceTrend.tone)">
                                            <font-awesome-icon v-if="totalDistanceTrend.direction === 'up'"
                                                :icon="['fas', 'arrow-trend-up']"
                                                class="mission-analytics-trend-icon" />
                                            <font-awesome-icon v-else-if="totalDistanceTrend.direction === 'down'"
                                                :icon="['fas', 'arrow-trend-down']"
                                                class="mission-analytics-trend-icon" />
                                            <font-awesome-icon v-else :icon="['fas', 'minus']"
                                                class="mission-analytics-trend-icon" />
                                            <span>{{ totalDistanceTrend.label }}</span>
                                        </div>
                                    </div>

                                    <div class="mission-analytics-label">Distance totale</div>
                                    <div class="mission-analytics-subtitle">Distance parcourue en mission</div>
                                </div>

                                <div class="mission-analytics-block">
                                    <div class="mission-analytics-value-row">
                                        <div class="mission-analytics-value">{{ formattedAverageDistance }}</div>

                                        <div v-if="averageDistanceTrend && averageDistanceTrend.label"
                                            class="mission-analytics-trend"
                                            :class="getTrendToneClass(averageDistanceTrend.tone)">
                                            <font-awesome-icon v-if="averageDistanceTrend.direction === 'up'"
                                                :icon="['fas', 'arrow-trend-up']"
                                                class="mission-analytics-trend-icon" />
                                            <font-awesome-icon v-else-if="averageDistanceTrend.direction === 'down'"
                                                :icon="['fas', 'arrow-trend-down']"
                                                class="mission-analytics-trend-icon" />
                                            <font-awesome-icon v-else :icon="['fas', 'minus']"
                                                class="mission-analytics-trend-icon" />
                                            <span>{{ averageDistanceTrend.label }}</span>
                                        </div>
                                    </div>

                                    <div class="mission-analytics-label">Distance moyenne</div>
                                    <div class="mission-analytics-subtitle">Par mission terminée</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-12 col-xl-4">
                <div class="card shadow-sm panel mb-3">
                    <div class="card-body">
                        <div class="panel-title">Répartition des alarmes</div>
                        <div class="panel-subtitle text-muted small mb-3">
                            Sur les {{ days }} derniers jours
                        </div>

                        <div class="mini-chart-placeholder">
                            <VChart v-if="stats.currentPeriod.criticalAlarmsByType.length" class="mini-chart"
                                :option="alarmTypesChartOptions" autoresize />
                            <div v-else class="text-muted small">
                                Aucune alarme sur la période
                            </div>
                        </div>
                    </div>
                </div>

                <div class="card shadow-sm panel">
                    <div class="card-body">
                        <div class="panel-title">Repères de la période</div>
                        <div class="panel-subtitle text-muted small mb-3">
                            Quelques repères pour mettre l’activité en perspective
                        </div>

                        <div class="insight-list">
                            <div class="insight-item">
                                <div class="insight-icon-wrap insight-icon-wrap-route">
                                    <font-awesome-icon :icon="['fas', 'route']" class="insight-icon" />
                                </div>

                                <div class="insight-content">
                                    <div class="insight-main" v-html="corridoringInsightHtml"></div>
                                    <div class="insight-subtext">
                                        Basé sur les alarmes de sortie de corridoring et la distance totale parcourue
                                    </div>
                                </div>
                            </div>

                            <div class="insight-item">
                                <div class="insight-icon-wrap insight-icon-wrap-sos">
                                    <font-awesome-icon :icon="['fas', 'life-ring']" class="insight-icon" />
                                </div>

                                <div class="insight-content">
                                    <div class="insight-main" v-html="sosInsightHtml"></div>
                                    <div class="insight-subtext">
                                        Calculé à partir des alarmes bouton SOS sur les {{ days }} derniers jours
                                    </div>
                                </div>
                            </div>

                            <div class="insight-item">
                                <div class="insight-icon-wrap">
                                    <font-awesome-icon :icon="['fas', 'clock']" class="insight-icon" />
                                </div>

                                <div class="insight-content">
                                    <div class="insight-main" v-html="averageDurationInsightHtml"></div>
                                    <div class="insight-subtext">
                                        Basé sur les missions terminées de la période
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import StatCard from "../components/StatCard.vue";
import StatService from "@/services/stat.service.js";
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart, BarChart } from "echarts/charts";
import { TooltipComponent, LegendComponent, GridComponent } from "echarts/components";

use([
    CanvasRenderer,
    PieChart,
    BarChart,
    TooltipComponent,
    LegendComponent,
    GridComponent,
]);

function getEmptyStats() {
    return {
        currentPeriod: {
            startDate: null,
            endDate: null,
            missionsCount: "—",
            alarmsCount: "—",
            balisesCount: "—",
            criticalAlarmsByType: [],
            criticalAlarmsTimeline: [],
            missionStats: {
                totalDurationSeconds: 0,
                averageDurationSeconds: 0,
                totalDistance: 0,
                averageDistance: 0,
                finishedMissionsCount: 0,
            },
        },
        previousPeriod: {
            startDate: null,
            endDate: null,
            missionsCount: 0,
            alarmsCount: 0,
            balisesCount: null,
            criticalAlarmsByType: [],
            criticalAlarmsTimeline: [],
            missionStats: {
                totalDurationSeconds: 0,
                averageDurationSeconds: 0,
                totalDistance: 0,
                averageDistance: 0,
                finishedMissionsCount: 0,
            },
        },
    };
}

export default {
    name: "OverviewPanel",
    components: {
        StatCard,
        VChart,
    },
    props: {
        clientId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            days: 7,
            stats: getEmptyStats(),
        };
    },
    computed: {
        periodSubtitle() {
            return `Sur les ${this.days} derniers jours`;
        },
        criticalAlarmsTimelineChartOptions() {
            return {
                tooltip: {
                    trigger: "axis",
                    axisPointer: {
                        type: "shadow",
                    },
                    formatter: (params) => {
                        const item = params?.[0];

                        if (!item) {
                            return "";
                        }

                        return `${item.axisValueLabel}<br/>${item.data} alarme(s)`;
                    },
                },
                grid: {
                    top: 16,
                    right: 12,
                    bottom: 24,
                    left: 36,
                    containLabel: true,
                },
                xAxis: {
                    type: "category",
                    axisTick: {
                        show: false,
                    },
                    axisLine: {
                        show: false,
                    },
                    data: this.stats.currentPeriod.criticalAlarmsTimeline.map((item) => {
                        const [, month, day] = item.date.split("-");
                        return `${day}/${month}`;
                    }),
                },
                yAxis: {
                    type: "value",
                    minInterval: 1,
                    splitLine: {
                        show: true,
                    },
                    axisLine: {
                        show: false,
                    },
                },
                series: [
                    {
                        type: "bar",
                        barMaxWidth: 22,
                        data: this.stats.currentPeriod.criticalAlarmsTimeline.map((item) => item.count),
                    },
                ],
            };
        },
        alarmTypesChartOptions() {
            return {
                tooltip: {
                    trigger: "item",
                    formatter: "{b}<br/>{c} alarme(s) ({d}%)",
                },
                legend: {
                    bottom: 0,
                    left: "center",
                    type: "scroll",
                },
                series: [
                    {
                        type: "pie",
                        radius: ["45%", "72%"],
                        center: ["50%", "42%"],
                        avoidLabelOverlap: true,
                        label: {
                            show: true,
                            formatter: "{c}",
                        },
                        labelLine: {
                            show: true,
                        },
                        data: this.stats.currentPeriod.criticalAlarmsByType.map((item) => ({
                            value: item.count,
                            name: item.name,
                        })),
                    },
                ],
            };
        },
        corridoringExitCount() {
            return this.getAlarmTypeCount(39);
        },
        sosAlarmCount() {
            return this.getAlarmTypeCount(40);
        },
        formattedTotalDuration() {
            return this.formatDuration(this.stats.currentPeriod.missionStats.totalDurationSeconds);
        },
        formattedAverageDuration() {
            return this.formatDuration(this.stats.currentPeriod.missionStats.averageDurationSeconds);
        },
        formattedTotalDistance() {
            return this.formatDistance(this.stats.currentPeriod.missionStats.totalDistance);
        },
        formattedAverageDistance() {
            return this.formatDistance(this.stats.currentPeriod.missionStats.averageDistance);
        },
        formattedAverageAlarmsPerMission() {
            const alarmsCount = Number(this.stats.currentPeriod.alarmsCount);
            const missionsCount = Number(this.stats.currentPeriod.missionsCount);

            if (!Number.isFinite(alarmsCount) || !Number.isFinite(missionsCount) || missionsCount <= 0) {
                return "—";
            }

            return (alarmsCount / missionsCount).toFixed(1).replace(".", ",");
        },
        averageAlarmsPerMissionSubtitle() {
            const missionsCount = Number(this.stats.currentPeriod.missionsCount);

            if (!Number.isFinite(missionsCount) || missionsCount <= 0) {
                return "Aucune mission sur la période";
            }

            return this.periodSubtitle;
        },
        corridoringInsightHtml() {
            const exits = this.corridoringExitCount;
            const totalDistance = Number(this.stats.currentPeriod.missionStats.totalDistance) || 0;

            if (exits <= 0) {
                return `Aucune <span class="insight-highlight">sortie de corridoring</span>`;
            }

            if (totalDistance <= 0) {
                return `<span class="insight-highlight">${exits} sortie(s) de corridoring</span> sur la période`;
            }

            const kmPerExit = totalDistance / exits / 1000;

            return `Une sortie de corridoring tous les <span class="insight-highlight">${this.formatKmValue(kmPerExit)}</span>`;
        },
        sosInsightHtml() {
            const count = this.sosAlarmCount;
            const perDay = count / this.days;

            if (count <= 0) {
                return `<span class="insight-highlight">Aucune alarme SOS</span>`;
            }

            if (perDay < 0.1) {
                return `<span class="insight-highlight">${count} alarme(s) SOS</span> sur la période`;
            }

            return `<span class="insight-highlight">${perDay.toFixed(1).replace(".", ",")} alarme(s) SOS par jour</span>`;
        },
        averageDurationInsightHtml() {
            return `Durée moyenne d’une mission : <span class="insight-highlight">${this.formattedAverageDuration}</span>`;
        },
        missionsTrend() {
            return this.buildTrend(
                this.stats.currentPeriod.missionsCount,
                this.stats.previousPeriod.missionsCount,
                {
                    upTone: "neutral",
                    downTone: "neutral",
                    zeroTone: "neutral",
                    newTone: "neutral",
                }
            );
        },
        alarmsTrend() {
            return this.buildTrend(
                this.stats.currentPeriod.alarmsCount,
                this.stats.previousPeriod.alarmsCount,
                {
                    upTone: "negative",
                    downTone: "positive",
                    zeroTone: "neutral",
                    newTone: "negative",
                }
            );
        },
        averageAlarmsPerMissionTrend() {
            return this.buildRatioTrend(
                this.stats.currentPeriod.alarmsCount,
                this.stats.currentPeriod.missionsCount,
                this.stats.previousPeriod.alarmsCount,
                this.stats.previousPeriod.missionsCount,
                {
                    upTone: "negative",
                    downTone: "positive",
                    zeroTone: "neutral",
                    newTone: "negative",
                }
            );
        },
        balisesTrend() {
            return this.buildTrend(
                this.stats.currentPeriod.balisesCount,
                this.stats.previousPeriod.balisesCount,
                {
                    upTone: "neutral",
                    downTone: "neutral",
                    zeroTone: "neutral",
                    newTone: "neutral",
                }
            );
        },
        totalDurationTrend() {
            return this.buildTrend(
                this.stats.currentPeriod.missionStats.totalDurationSeconds,
                this.stats.previousPeriod.missionStats.totalDurationSeconds,
                {
                    upTone: "positive",
                    downTone: "negative",
                    zeroTone: "neutral",
                    newTone: "positive",
                }
            );
        },
        averageDurationTrend() {
            return this.buildTrend(
                this.stats.currentPeriod.missionStats.averageDurationSeconds,
                this.stats.previousPeriod.missionStats.averageDurationSeconds,
                {
                    upTone: "positive",
                    downTone: "negative",
                    zeroTone: "neutral",
                    newTone: "positive",
                }
            );
        },
        totalDistanceTrend() {
            return this.buildTrend(
                this.stats.currentPeriod.missionStats.totalDistance,
                this.stats.previousPeriod.missionStats.totalDistance,
                {
                    upTone: "positive",
                    downTone: "negative",
                    zeroTone: "neutral",
                    newTone: "positive",
                }
            );
        },
        averageDistanceTrend() {
            return this.buildTrend(
                this.stats.currentPeriod.missionStats.averageDistance,
                this.stats.previousPeriod.missionStats.averageDistance,
                {
                    upTone: "positive",
                    downTone: "negative",
                    zeroTone: "neutral",
                    newTone: "positive",
                }
            );
        },
    },
    watch: {
        clientId: {
            immediate: true,
            handler() {
                this.fetchStats();
            },
        },
    },
    methods: {
        getAlarmTypeCount(alarmTypeId) {
            const items = this.stats.currentPeriod.criticalAlarmsByType || [];
            const match = items.find((item) => Number(item.alarmTypeId) === Number(alarmTypeId));
            return Number(match?.count) || 0;
        },
        formatDuration(totalSeconds) {
            const seconds = Number(totalSeconds) || 0;
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);

            if (hours <= 0) {
                return `${minutes} min`;
            }

            if (minutes <= 0) {
                return `${hours} h`;
            }

            return `${hours} h ${minutes < 10 ? "0" : ""}${minutes}`;
        },
        formatDistance(distanceMeters) {
            const meters = Number(distanceMeters) || 0;

            if (meters < 1000) {
                return `${meters.toLocaleString("fr-FR")} m`;
            }

            const km = meters / 1000;

            if (km < 10) {
                return `${km.toFixed(1).replace(".", ",")} km`;
            }

            return `${Math.round(km).toLocaleString("fr-FR")} km`;
        },
        formatKmValue(kmValue) {
            const km = Number(kmValue) || 0;

            if (km < 10) {
                return `${km.toFixed(1).replace(".", ",")} km`;
            }

            return `${Math.round(km).toLocaleString("fr-FR")} km`;
        },
        getTrendToneClass(tone) {
            if (tone === "positive") {
                return "mission-analytics-trend-positive";
            }

            if (tone === "negative") {
                return "mission-analytics-trend-negative";
            }

            return "mission-analytics-trend-neutral";
        },
        buildTrend(currentValue, previousValue, tones = {}) {
            if (
                previousValue === null ||
                previousValue === undefined ||
                currentValue === null ||
                currentValue === undefined
            ) {
                return null;
            }

            const current = Number(currentValue);
            const previous = Number(previousValue);

            if (!Number.isFinite(current) || !Number.isFinite(previous)) {
                return null;
            }

            if (previous === 0) {
                if (current === 0) {
                    return {
                        previousValue: previous,
                        currentValue: current,
                        delta: 0,
                        percentage: 0,
                        direction: "neutral",
                        tone: tones.zeroTone || "neutral",
                        label: "0%",
                    };
                }

                return {
                    previousValue: previous,
                    currentValue: current,
                    delta: current,
                    percentage: null,
                    direction: "up",
                    tone: tones.newTone || "neutral",
                    label: "Nouveau",
                };
            }

            const delta = current - previous;
            const percentage = (delta / previous) * 100;
            const roundedPercentage = Math.round(percentage);

            return {
                previousValue: previous,
                currentValue: current,
                delta,
                percentage,
                direction: delta > 0 ? "up" : delta < 0 ? "down" : "neutral",
                tone: delta > 0
                    ? (tones.upTone || "neutral")
                    : delta < 0
                        ? (tones.downTone || "neutral")
                        : (tones.zeroTone || "neutral"),
                label: `${roundedPercentage > 0 ? "+" : ""}${roundedPercentage}%`,
            };
        },
        buildRatioTrend(currentNumerator, currentDenominator, previousNumerator, previousDenominator, tones = {}) {
            if (
                currentNumerator === null ||
                currentNumerator === undefined ||
                currentDenominator === null ||
                currentDenominator === undefined ||
                previousNumerator === null ||
                previousNumerator === undefined ||
                previousDenominator === null ||
                previousDenominator === undefined
            ) {
                return null;
            }

            const currentNum = Number(currentNumerator);
            const currentDen = Number(currentDenominator);
            const previousNum = Number(previousNumerator);
            const previousDen = Number(previousDenominator);

            if (
                !Number.isFinite(currentNum) ||
                !Number.isFinite(currentDen) ||
                !Number.isFinite(previousNum) ||
                !Number.isFinite(previousDen)
            ) {
                return null;
            }

            if (currentDen <= 0 || previousDen <= 0) {
                return null;
            }

            const currentRatio = currentNum / currentDen;
            const previousRatio = previousNum / previousDen;

            return this.buildTrend(currentRatio, previousRatio, tones);
        },
        async fetchStats() {
            if (!this.clientId) {
                this.stats = getEmptyStats();
                return;
            }

            try {
                const res = await StatService.getClientStats(this.clientId, this.days);
                const data = res?.data || {};

                this.stats = {
                    currentPeriod: {
                        startDate: data.currentPeriod?.startDate ?? null,
                        endDate: data.currentPeriod?.endDate ?? null,
                        missionsCount: data.currentPeriod?.missionsCount ?? 0,
                        alarmsCount: data.currentPeriod?.alarmsCount ?? 0,
                        balisesCount: data.currentPeriod?.balisesCount ?? 0,
                        criticalAlarmsByType: data.currentPeriod?.criticalAlarmsByType ?? [],
                        criticalAlarmsTimeline: data.currentPeriod?.criticalAlarmsTimeline ?? [],
                        missionStats: {
                            totalDurationSeconds: data.currentPeriod?.missionStats?.totalDurationSeconds ?? 0,
                            averageDurationSeconds: data.currentPeriod?.missionStats?.averageDurationSeconds ?? 0,
                            totalDistance: data.currentPeriod?.missionStats?.totalDistance ?? 0,
                            averageDistance: data.currentPeriod?.missionStats?.averageDistance ?? 0,
                            finishedMissionsCount: data.currentPeriod?.missionStats?.finishedMissionsCount ?? 0,
                        },
                    },
                    previousPeriod: {
                        startDate: data.previousPeriod?.startDate ?? null,
                        endDate: data.previousPeriod?.endDate ?? null,
                        missionsCount: data.previousPeriod?.missionsCount ?? 0,
                        alarmsCount: data.previousPeriod?.alarmsCount ?? 0,
                        balisesCount: data.previousPeriod?.balisesCount ?? null,
                        criticalAlarmsByType: data.previousPeriod?.criticalAlarmsByType ?? [],
                        criticalAlarmsTimeline: data.previousPeriod?.criticalAlarmsTimeline ?? [],
                        missionStats: {
                            totalDurationSeconds: data.previousPeriod?.missionStats?.totalDurationSeconds ?? 0,
                            averageDurationSeconds: data.previousPeriod?.missionStats?.averageDurationSeconds ?? 0,
                            totalDistance: data.previousPeriod?.missionStats?.totalDistance ?? 0,
                            averageDistance: data.previousPeriod?.missionStats?.averageDistance ?? 0,
                            finishedMissionsCount: data.previousPeriod?.missionStats?.finishedMissionsCount ?? 0,
                        },
                    },
                };
            } catch (_e) {
                this.stats = getEmptyStats();
            }
        },
    },
};
</script>

<style scoped>
.tab-panel {
    margin-top: 4px;
}

.panel {
    border-radius: 16px;
    border: 1px solid rgba(15, 23, 42, 0.08);
}

.panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.panel-title {
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
}

.panel-subtitle {
    line-height: 1.4;
}

.chart-placeholder {
    position: relative;
    overflow: hidden;
    border-radius: 14px;
    border: 1px dashed rgba(15, 23, 42, 0.15);
    background:
        linear-gradient(180deg, rgba(13, 110, 253, 0.04), rgba(13, 110, 253, 0.01)),
        #f8fafc;
}

.chart-placeholder-lg {
    height: 360px;
    padding: 18px;
}

.main-chart {
    width: 100%;
    height: 100%;
}

.mini-chart-placeholder {
    min-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    border: 1px dashed rgba(15, 23, 42, 0.15);
    background: #f8fafc;
}

.mini-chart {
    width: 100%;
    height: 260px;
}

.insight-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.insight-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 8px;
    border-radius: 10px;
    transition: background 0.2s ease;
}

.insight-item:hover {
    background: rgba(15, 23, 42, 0.04);
}

.insight-icon-wrap {
    width: 34px;
    height: 34px;
    min-width: 34px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.05);
    border: 1px solid rgba(15, 23, 42, 0.06);
}

.insight-icon-wrap-route {
    background: rgba(14, 165, 233, 0.10);
    border-color: rgba(14, 165, 233, 0.14);
}

.insight-icon-wrap-sos {
    background: rgba(239, 68, 68, 0.10);
    border-color: rgba(239, 68, 68, 0.14);
}

.insight-icon-wrap-duration {
    background: rgba(16, 185, 129, 0.10);
    border-color: rgba(16, 185, 129, 0.14);
}

.insight-icon {
    font-size: 0.95rem;
    color: #475569;
}

.insight-content {
    min-width: 0;
}

.insight-main {
    color: #334155;
    font-size: 0.96rem;
    line-height: 1.45;
    font-weight: 500;
}

.insight-highlight {
    font-weight: 800;
    color: #0f172a;
}

.insight-subtext {
    margin-top: 4px;
    color: #64748b;
    font-size: 0.82rem;
    line-height: 1.4;
}

.mission-analytics {
    display: grid;
    grid-template-columns: 1fr 1px 1fr;
    gap: 24px;
    align-items: stretch;
}

.mission-analytics-column {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.mission-analytics-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #334155;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.mission-analytics-separator {
    width: 1px;
    background: rgba(15, 23, 42, 0.08);
}

.mission-analytics-block {
    padding: 20px 6px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.mission-analytics-block:last-child {
    border-bottom: none;
}

.mission-analytics-value-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 6px;
    flex-wrap: wrap;
}

.mission-analytics-value {
    font-size: 2rem;
    line-height: 1.05;
    font-weight: 700;
    color: #0f172a;
}

.mission-analytics-trend {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.95rem;
    line-height: 1;
    font-weight: 700;
}

.mission-analytics-trend-icon {
    font-size: 1rem;
}

.mission-analytics-trend-positive {
    color: #16a34a;
}

.mission-analytics-trend-negative {
    color: #dc2626;
}

.mission-analytics-trend-neutral {
    color: #64748b;
}

.mission-analytics-label {
    font-size: 0.95rem;
    font-weight: 600;
    color: #334155;
    margin-bottom: 4px;
}

.mission-analytics-subtitle {
    font-size: 0.85rem;
    color: #64748b;
    line-height: 1.4;
}

@media (max-width: 991px) {
    .chart-placeholder-lg {
        height: 260px;
    }

    .mission-analytics {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .mission-analytics-separator {
        display: none;
    }
}
</style>